import type { AuthError } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { invokeEdgeFunction } from '../observability/invokeEdgeFunction';
import { logClientEvent } from '../observability/logClientEvent';
import { signInWithEmailPassword } from './login';

/** Unified copy for Forgot flows — do not reveal whether the account exists. */
export const PASSWORD_RESET_REQUEST_ACK =
  'If that contact is registered, we sent password reset instructions. Check spam if you do not see it, or contact your dispatcher / UBUS support.';

export const PASSWORD_RESET_REQUEST_ACK_ZH =
  '若该联系方式已登记，我们将发送重置说明。未收到请检查垃圾箱，或联系调度 / UBUS 支持。';

export async function requestPasswordReset(
  email: string,
  redirectTo: string
): Promise<{ ok: true }> {
  const trimmed = email.trim().toLowerCase();
  // Always acknowledge — anti-enumeration (ignore Auth errors for UX).
  try {
    await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo });
  } catch {
    // swallow
  }
  return { ok: true };
}

export async function updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
}

/**
 * Verify current password then set a new one. Signs the user back in with the new password
 * so the session remains valid after update.
 */
export async function changePassword(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<{ error: AuthError | Error | null }> {
  const verify = await signInWithEmailPassword(email.trim(), currentPassword);
  if (verify.error) {
    return { error: verify.error };
  }
  const { error } = await updatePassword(newPassword);
  if (error) return { error };
  // Refresh session with new credentials
  await signInWithEmailPassword(email.trim(), newPassword);
  return { error: null };
}

export function resetPasswordRedirectUrl(origin?: string): string {
  const base = (origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(
    /\/$/,
    ''
  );
  return `${base}/reset-password`;
}

/** Map admin portal origin → driver H5 reset landing (intranet ports / hostnames). */
export function driverResetPasswordRedirectUrl(origin?: string): string {
  const base = (origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(
    /\/$/,
    ''
  );
  const driverBase = base
    .replace(':18080', ':18081')
    .replace('ubus-admin', 'ubus-driver')
    .replace(':3001', ':3002');
  return `${driverBase}/reset-password`;
}

/** Map admin portal origin → client portal reset landing. */
export function clientResetPasswordRedirectUrl(origin?: string): string {
  const base = (origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(
    /\/$/,
    ''
  );
  const clientBase = base
    .replace(':18080', ':18082')
    .replace('ubus-admin', 'ubus-client')
    .replace(':3001', ':3003');
  return `${clientBase}/reset-password`;
}

export interface SendPasswordSetupParams {
  driverId?: string;
  email?: string;
  /** Target auth uid — required for fleet-primary callers to bind the email. */
  userId?: string;
  redirectTo: string;
  /** When false, only generate link (admin copies). Default true. */
  sendEmail?: boolean;
}

export interface SendPasswordSetupResult {
  setupLink: string | null;
  emailed: boolean;
  recipient: string | null;
  email: string;
}

export async function sendPasswordSetupLink(
  params: SendPasswordSetupParams
): Promise<SendPasswordSetupResult> {
  const { data, error } = await invokeEdgeFunction<SendPasswordSetupResult & { error?: string }>(
    'send-password-setup',
    {
      body: {
        driver_id: params.driverId,
        email: params.email,
        user_id: params.userId,
        redirect_to: params.redirectTo,
        send_email: params.sendEmail !== false,
      },
    }
  );

  if (error) {
    throw error;
  }

  if (!data || typeof data !== 'object' || 'error' in data) {
    const message =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : 'Failed to send password setup link';
    throw new Error(message);
  }

  return data as SendPasswordSetupResult;
}

export function logAssistResetAudit(entry: {
  actorId?: string | null;
  targetType: string;
  targetId?: string | null;
  channel: string;
  emailed: boolean;
}): void {
  // CONF: application structured log (no DB table required for Now)
  logClientEvent('auth.assist_reset', entry);
}
